'use client';
import React, { useState, useEffect, Suspense, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { adoptionInquiryService } from '../../services/adoptionInquiryService';
import { petService } from '../../utils/petService';
import { useAuth } from '@/contexts/AuthContext';
import { Pet } from '../../types/models';

// Separate component that uses useSearchParams
function AdoptionInquiryForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const petId = searchParams?.get('petId');
  const { user } = useAuth();

  const [selectedPet, setSelectedPet] = useState<Pet | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [formData, setFormData] = useState({
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    preferredContact: 'email' as 'email' | 'phone' | 'both',
    petQuestions: '',
    concerns: '',
    specialInterests: '',
    housingType: '' as 'house' | 'apartment' | 'condo' | 'farm' | 'other' | '',
    hasYard: false,
    hasOtherPets: false,
    otherPetsDetails: '',
    hasChildren: false,
    childrenAges: '',
    experienceLevel: '',
    adoptionTimeline: '',
    employmentStatus: '',
    employerName: '',
    occupation: '',
    workSchedule: '',
    vetName: '',
    vetPhone: '',
    vetAddress: '',
    landlordName: '',
    landlordPhone: '',
    landlordEmail: '',
    petPolicyApproved: false,
    whatsappNumber: '',
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [characterCounts, setCharacterCounts] = useState({
    petQuestions: 0,
    concerns: 0,
    specialInterests: 0,
  });

  React.useEffect(() => {
    const loadPet = async () => {
      if (petId) {
        try {
          const pet = await petService.getById(petId);
          setSelectedPet(pet);
        } catch (err) {
          setError('Failed to load pet details');
        }
      }
    };
    loadPet();
  }, [petId]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        contactName: user.user_metadata?.full_name || '',
        contactEmail: user.email || '',
        contactPhone: user.phone || '',
      }));
    }
  }, [user]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (['petQuestions', 'concerns', 'specialInterests'].includes(name)) {
      setCharacterCounts(prev => ({
        ...prev,
        [name]: value?.length || 0,
      }));
    }

    setValidationErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.contactName?.trim()) {
      errors.contactName = 'Name is required';
    }

    if (!formData.contactEmail?.trim()) {
      errors.contactEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      errors.contactEmail = 'Please enter a valid email address';
    }

    if (formData.preferredContact === 'phone' || formData.preferredContact === 'both') {
      if (!formData.contactPhone?.trim()) {
        errors.contactPhone = 'Phone number is required for phone contact';
      }
    }

    if (!formData.housingType) {
      errors.housingType = 'Please select your housing type';
    }

    if (!formData.experienceLevel?.trim()) {
      errors.experienceLevel = 'Please describe your experience level';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Please fix the errors in the form');
      return;
    }

    if (!petId) {
      setError('No pet selected for inquiry');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await adoptionInquiryService.create({
        petId,
        userId: user?.id || '',
        contactName: formData.contactName,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone || undefined,
        preferredContact: formData.preferredContact,
        petQuestions: formData.petQuestions || undefined,
        concerns: formData.concerns || undefined,
        specialInterests: formData.specialInterests || undefined,
        housingType: formData.housingType as 'house' | 'apartment' | 'condo' | 'farm' | 'other' | undefined,
        hasYard: formData.hasYard,
        hasOtherPets: formData.hasOtherPets,
        otherPetsDetails: formData.otherPetsDetails || undefined,
        hasChildren: formData.hasChildren,
        childrenAges: formData.childrenAges || undefined,
        experienceLevel: formData.experienceLevel || undefined,
        adoptionTimeline: formData.adoptionTimeline || undefined,
        employmentStatus: formData.employmentStatus || undefined,
        employerName: formData.employerName || undefined,
        occupation: formData.occupation || undefined,
        workSchedule: formData.workSchedule || undefined,
        vetName: formData.vetName || undefined,
        vetPhone: formData.vetPhone || undefined,
        vetAddress: formData.vetAddress || undefined,
        landlordName: formData.landlordName || undefined,
        landlordPhone: formData.landlordPhone || undefined,
        landlordEmail: formData.landlordEmail || undefined,
        petPolicyApproved: formData.petPolicyApproved,
        whatsappNumber: formData.whatsappNumber || undefined,
      });

      setSuccess(true);
      timeoutRef.current = setTimeout(() => {
        router.push('/adoption-process');
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit inquiry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Adoption Inquiry
            </h1>
            <p className="text-lg text-gray-600">
              Start your journey to bring home your new companion
            </p>
          </div>

          {/* Privacy Assurance */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-primary mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">Your Privacy Matters</h3>
                <p className="text-sm text-blue-800">
                  Your information is secure and will only be shared with the shelter.
                  Expect a response within 2-3 business days.
                </p>
              </div>
            </div>
          </div>

          {/* Selected Pet Display */}
          {selectedPet && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Inquiring About
              </h2>
              <div className="flex items-center space-x-4">
                <img
                  src={selectedPet.imageUrl || '/assets/images/no_image.png'}
                  alt={selectedPet.name || 'Pet'}
                  className="w-24 h-24 object-cover rounded-lg"
                />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedPet.name}
                  </h3>
                  <p className="text-gray-600">{selectedPet.breed}</p>
                  <p className="text-sm text-gray-500">
                    {selectedPet.shelterLocation}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-green-900 mb-2">Inquiry Submitted Successfully!</h3>
              <p className="text-green-800">
                The shelter has received your inquiry and will contact you within 2-3 business days.
                Redirecting you to the adoption process page...
              </p>
            </div>
          )}

          {/* Inquiry Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
            {/* Contact Details Section */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Contact Details
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent ${validationErrors.contactName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Enter your full name"
                  />
                  {validationErrors.contactName && (
                    <p className="text-sm text-red-500 mt-1">{validationErrors.contactName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent ${validationErrors.contactEmail ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="your.email@example.com"
                  />
                  {validationErrors.contactEmail && (
                    <p className="text-sm text-red-500 mt-1">{validationErrors.contactEmail}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent ${validationErrors.contactPhone ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="+1 (555) 123-4567"
                  />
                  {validationErrors.contactPhone && (
                    <p className="text-sm text-red-500 mt-1">{validationErrors.contactPhone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Contact Method <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="preferredContact"
                    value={formData.preferredContact}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="both">Both Email and Phone</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Pet-Specific Questions Section */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                About This Pet
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Questions About This Pet
                  </label>
                  <textarea
                    name="petQuestions"
                    value={formData.petQuestions}
                    onChange={handleInputChange}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                    placeholder="Ask about temperament, training, medical history, etc."
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {characterCounts.petQuestions}/500 characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Any Concerns or Special Considerations
                  </label>
                  <textarea
                    name="concerns"
                    value={formData.concerns}
                    onChange={handleInputChange}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                    placeholder="Allergies, work schedule, travel frequency, etc."
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {characterCounts.concerns}/500 characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    What Interests You Most About This Pet
                  </label>
                  <textarea
                    name="specialInterests"
                    value={formData.specialInterests}
                    onChange={handleInputChange}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                    placeholder="Personality traits, appearance, energy level, etc."
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {characterCounts.specialInterests}/500 characters
                  </p>
                </div>
              </div>
            </div>

            {/* Adoption Readiness Section */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Adoption Readiness Assessment
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Housing Situation <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="housingType"
                    value={formData.housingType}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent ${validationErrors.housingType ? 'border-red-500' : 'border-gray-300'
                      }`}
                  >
                    <option value="">Select housing type</option>
                    <option value="house">House</option>
                    <option value="apartment">Apartment</option>
                    <option value="condo">Condo</option>
                    <option value="farm">Farm</option>
                    <option value="other">Other</option>
                  </select>
                  {validationErrors.housingType && (
                    <p className="text-sm text-red-500 mt-1">{validationErrors.housingType}</p>
                  )}
                </div>

                <div className="flex items-center space-x-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="hasYard"
                      checked={formData.hasYard}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-secondary"
                    />
                    <span className="ml-2 text-sm text-gray-700">Have a yard</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="hasOtherPets"
                      checked={formData.hasOtherPets}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-secondary"
                    />
                    <span className="ml-2 text-sm text-gray-700">Have other pets</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="hasChildren"
                      checked={formData.hasChildren}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-secondary"
                    />
                    <span className="ml-2 text-sm text-gray-700">Have children</span>
                  </label>
                </div>

                {formData.hasOtherPets && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tell us about your other pets
                    </label>
                    <input
                      type="text"
                      name="otherPetsDetails"
                      value={formData.otherPetsDetails}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                      placeholder="Species, breeds, ages, temperament"
                    />
                  </div>
                )}

                {formData.hasChildren && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ages of children
                    </label>
                    <input
                      type="text"
                      name="childrenAges"
                      value={formData.childrenAges}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                      placeholder="e.g., 5, 8, 12 years old"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pet Ownership Experience <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="experienceLevel"
                    value={formData.experienceLevel}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent ${validationErrors.experienceLevel ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="e.g., First-time owner, 10 years with dogs, etc."
                  />
                  {validationErrors.experienceLevel && (
                    <p className="text-sm text-red-500 mt-1">{validationErrors.experienceLevel}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adoption Timeline
                  </label>
                  <input
                    type="text"
                    name="adoptionTimeline"
                    value={formData.adoptionTimeline}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                    placeholder="e.g., Within 2 weeks, Next month, Just researching"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-colors ${loading
                  ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-secondary'
                  }`}
              >
                {loading ? 'Submitting...' : 'Submit Inquiry'}
              </button>
            </div>

            {/* Next Steps Information */}
            <div className="bg-gray-50 rounded-lg p-4 mt-4">
              <h3 className="font-semibold text-gray-900 mb-2">What Happens Next?</h3>
              <ol className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="font-semibold mr-2">1.</span>
                  <span>Shelter reviews your inquiry within 2-3 business days</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">2.</span>
                  <span>You will receive an email or call to schedule a meet-and-greet</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">3.</span>
                  <span>Complete the adoption application and home assessment</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">4.</span>
                  <span>Finalize adoption and welcome your new companion home!</span>
                </li>
              </ol>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

// Main page component with Suspense boundary
export default function AdoptionInquiryPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    }>
      <AdoptionInquiryForm />
    </Suspense>
  );
}
